import {
  useMutation,
  type ApolloCache,
  type FetchResult,
} from "@apollo/client";

import { UPDATE_HEALTH_LEAF_METADATA } from "../../../common/operations/mutations";
import { handleApolloError } from "../../../common/utils/handleApolloError";
import { errorNotification } from "../../../common/variables/notification";

interface UpdateHealthLeafMetadataParams {
  pk1: string;
  sk: string;
  reason?: string;
  assignedWatcher?: "CLEANCONNECT" | "THIRD_PARTY" | "CUSTOMER";
}

interface UpdateHealthLeafMetadataResultShape {
  updateHealthLeafMetadata: {
    updatedCount: number;
  };
}

interface UpdateHealthLeafMetadataVariables {
  input: {
    pk1: string;
    sk: string;
    reason?: string;
    assignedWatcher?: "CLEANCONNECT" | "THIRD_PARTY" | "CUSTOMER";
  };
}

export const useUpdateHealthLeafMetadata = () => {
  const [mutate, { data, loading }] = useMutation<
    UpdateHealthLeafMetadataResultShape,
    UpdateHealthLeafMetadataVariables
  >(UPDATE_HEALTH_LEAF_METADATA);

  const updateHealthLeafMetadata = async (
    params: UpdateHealthLeafMetadataParams
  ): Promise<FetchResult<UpdateHealthLeafMetadataResultShape>> => {
    return await mutate({
      variables: {
        input: {
          pk1: params.pk1,
          sk: params.sk,
          ...(typeof params.reason !== "undefined"
            ? { reason: params.reason }
            : {}),
          ...(typeof params.assignedWatcher !== "undefined"
            ? { assignedWatcher: params.assignedWatcher }
            : {}),
        },
      },
      update: (cache: ApolloCache<UpdateHealthLeafMetadataResultShape>) => {
        try {
          cache.modify({
            fields: {
              listHealthLeaves(existing) {
                if (!existing) return existing;

                const items = (existing.items ?? []).map(
                  (item: Record<string, unknown>) => {
                    const itemPk = item?.pk ?? null;
                    const itemSk = item?.sk ?? null;

                    if (itemPk === params.pk1 && itemSk === params.sk) {
                      const next: Record<string, unknown> = { ...item };

                      if (typeof params.reason !== "undefined") {
                        next.reason =
                          params.reason === ""
                            ? null
                            : (params.reason as string);
                      }

                      if (typeof params.assignedWatcher !== "undefined") {
                        next.assignedWatcher = params.assignedWatcher;
                      }

                      return next;
                    }

                    return item;
                  }
                );

                return { ...existing, items };
              },
            },
          });
        } catch {
          // ignore cache update errors
        }
      },
      onCompleted: result => {
        const count = result?.updateHealthLeafMetadata?.updatedCount ?? 0;

        if (count === 0) {
          errorNotification("Nothing was updated.");
        }
      },
      onError: handleApolloError,
    });
  };

  return { updateHealthLeafMetadata, data, loading };
};
